<script lang="ts">
	import type { Report } from '#lib/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';
	import { qualityNote } from '#lib/i18n/quality';
	import { SEVERITY_UI, severityLabel, designLabel } from '#lib/ui/labels';
	import ScoreDial from './ScoreDial.svelte';
	import CategoryList from './CategoryList.svelte';

	interface Props {
		report: Report;
		onjump: (issueId: string) => void;
		onselect: (issueId: string) => void;
	}

	let { report, onjump, onselect }: Props = $props();

	const topIssues = $derived(
		report.issues
			.filter((i) => i.decisive)
			.sort((a, b) => {
				const rank = { critical: 0, warning: 1, info: 2, ok: 3 } as const;
				return rank[a.severity] - rank[b.severity];
			})
			.slice(0, 4)
	);

	function issueTitle(issue: Report['issues'][number]): string {
		return (i18n.lang === 'en' ? issueTextEn(issue)?.title : null) ?? issue.title;
	}

	const coverage = $derived([
		{
			label: t('overview.covStats'),
			value: report.stats.summary.total,
			note: t('overview.covStatsNote', {
				consistent: report.stats.summary.consistent,
				inconsistent: report.stats.summary.inconsistent
			})
		},
		{
			label: t('overview.covCitations'),
			value: report.citations.summary.citations,
			note: t('overview.covCitationsNote', {
				matched: report.citations.summary.matched,
				missing: report.citations.summary.missing
			})
		},
		{
			label: t('overview.covReferences'),
			value: report.citations.summary.references,
			note: t('overview.covReferencesNote', { orphans: report.citations.summary.orphans })
		},
		{
			label: t('overview.covFigures'),
			value: report.structure.summary.tables + report.structure.summary.figures,
			note: t('overview.covFiguresNote', { unreferenced: report.structure.summary.unreferenced })
		},
		{
			label: t('overview.covLanguage'),
			value: report.language.hits.length,
			note: t('overview.covLanguageNote', { causal: report.language.summary.unjustifiedCausal })
		}
	]);

	const qualityPct = $derived(Math.round(report.document.quality.score * 100));
	const overall = $derived(report.scores.overall);
	const grade = $derived(report.scores.grade);
	const critical = $derived(report.counts.critical);

	const verdict = $derived.by(() => {
		if (overall >= 90 && critical === 0) return t('overview.verdict.clean');
		if (critical > 0) return t('overview.verdict.critical', { n: critical });
		if (overall >= 75) return t('overview.verdict.good');
		if (overall >= 60) return t('overview.verdict.medium');
		return t('overview.verdict.bad', { score: overall, grade });
	});
</script>

<div class="overview">
	<section class="scorecard card">
		<div class="score-main">
			<ScoreDial score={overall} {grade} />
			<div class="summary">
				<p class="eyebrow">{t('overview.eyebrow')}</p>
				<p class="verdict">{@html verdict}</p>
				<div class="counts">
					<span class="chip chip-critical">{t('overview.critical', { n: report.counts.critical })}</span>
					<span class="chip chip-warning">{t('overview.warning', { n: report.counts.warning })}</span>
					<span class="chip chip-info">{t('overview.info', { n: report.counts.info })}</span>
				</div>
				{#if report.undecided > 0}
					<p class="undecided">
						{t('overview.undecided', { n: report.undecided })}
					</p>
				{/if}
			</div>
		</div>

		<div class="quality">
			<div class="row-between">
				<span class="eyebrow">{t('overview.qualityEyebrow')}</span>
				<span class="mono small">%{qualityPct}</span>
			</div>
			<div class="q-track" aria-hidden="true">
				<div
					class="q-fill"
					style:width="{qualityPct}%"
					style:background={qualityPct >= 75 ? '#0f766e' : qualityPct >= 45 ? '#9a5b06' : '#b3261e'}
				></div>
			</div>
			<p class="small muted q-note">
				{report.document.quality.notes[0]
					? qualityNote(report.document.quality.notes[0], i18n.lang)
					: t('overview.qualityOk')}
			</p>
		</div>
	</section>

	<div class="cols">
		<section class="card panel">
			<h3>{t('overview.categoriesTitle')}</h3>
			<p class="small muted panel-sub">
				{t('overview.categoriesSub')}
			</p>
			<CategoryList categories={report.scores.categories} />
		</section>

		<section class="card panel">
			<h3>{t('overview.priorityTitle')}</h3>
			<p class="small muted panel-sub">{t('overview.prioritySub', { n: topIssues.length })}</p>
			{#if topIssues.length === 0}
				<p class="empty">{t('overview.priorityNone')}</p>
			{:else}
				<ul class="top-list">
					{#each topIssues as issue (issue.id)}
						<li>
							<span
								class="dot"
								style:background={SEVERITY_UI[issue.severity].dot}
								aria-label={severityLabel(issue.severity)}
							></span>
							<button class="top-btn" onclick={() => onselect(issue.id)}>{issueTitle(issue)}</button>
							<button
								class="jump-link"
								onclick={() => onjump(issue.id)}
								title={t('issue.showInText')}
								aria-label={t('issue.showInText')}
							>
								↗
							</button>
						</li>
					{/each}
				</ul>
			{/if}

			<hr class="divider" />

			<h3>{t('overview.coverageTitle')}</h3>
			<ul class="coverage">
				{#each coverage as c (c.label)}
					<li>
						<span class="cov-label">{c.label}</span>
						<span class="cov-value mono">{c.value}</span>
						<span class="cov-note">{c.note}</span>
					</li>
				{/each}
			</ul>
			<p class="design small muted">
				{t('overview.design')} <strong>{designLabel(report.language.design)}</strong>
				{#if report.language.designEvidence}
					<span class="mono">({report.language.designEvidence})</span>
				{/if}
			</p>
		</section>
	</div>
</div>

<style>
	.overview {
		display: grid;
		gap: 1rem;
	}
	.scorecard {
		padding: clamp(1.2rem, 3vw, 1.8rem);
		display: grid;
		gap: 1.4rem;
		background:
			radial-gradient(120% 120% at 100% 0%, rgb(227 241 238 / 0.7), transparent 55%),
			var(--card);
	}
	.score-main {
		display: flex;
		gap: clamp(1rem, 3vw, 2.2rem);
		align-items: center;
		flex-wrap: wrap;
	}
	.summary {
		flex: 1;
		min-width: 240px;
	}
	.eyebrow {
		margin: 0 0 0.3rem;
		font-size: 0.7rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--muted);
	}
	.verdict {
		font-size: 1.02rem;
		line-height: 1.5;
		margin: 0 0 0.7rem;
		max-width: 46ch;
	}
	.counts {
		display: flex;
		gap: 0.4rem;
		flex-wrap: wrap;
	}
	.undecided {
		font-size: 0.8rem;
		color: var(--muted);
		margin: 0.7rem 0 0;
		max-width: 52ch;
	}
	.quality {
		border-top: 1px solid var(--line);
		padding-top: 1rem;
	}
	.q-track {
		height: 5px;
		border-radius: 99px;
		background: var(--paper-2);
		overflow: hidden;
		margin: 0.35rem 0 0.4rem;
	}
	.q-fill {
		height: 100%;
		border-radius: inherit;
		transition: width 0.8s var(--ease);
	}
	.q-note {
		margin: 0;
	}
	.cols {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
	}
	@media (max-width: 900px) {
		.cols {
			grid-template-columns: 1fr;
		}
	}
	.panel {
		padding: 1.25rem 1.3rem;
	}
	.panel h3 {
		font-family: var(--font-ui);
		font-size: 0.92rem;
		font-weight: 650;
		margin: 0;
	}
	.panel-sub {
		margin: 0.15rem 0 1rem;
	}
	.top-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.15rem;
	}
	.top-list li {
		display: flex;
		align-items: center;
		gap: 0.55rem;
		padding: 0.4rem 0;
		border-bottom: 1px solid var(--line);
	}
	.top-list li:last-child {
		border-bottom: 0;
	}
	.dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		flex: none;
	}
	.top-btn {
		flex: 1;
		text-align: left;
		background: none;
		border: 0;
		padding: 0;
		font-size: 0.86rem;
		cursor: pointer;
		line-height: 1.4;
	}
	.top-btn:hover {
		color: var(--accent-ink);
		text-decoration: underline;
	}
	.jump-link {
		border: 0;
		background: transparent;
		color: var(--muted);
		cursor: pointer;
		font-size: 0.9rem;
		padding: 0.1rem 0.25rem;
		border-radius: 5px;
	}
	.jump-link:hover {
		background: var(--paper-2);
		color: var(--accent-ink);
	}
	.coverage {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.45rem;
	}
	.coverage li {
		display: grid;
		grid-template-columns: 1fr auto;
		gap: 0 0.6rem;
		font-size: 0.83rem;
	}
	.cov-value {
		font-weight: 600;
	}
	.cov-note {
		grid-column: 1 / -1;
		color: var(--muted);
		font-size: 0.73rem;
	}
	.design {
		margin: 1rem 0 0;
	}
	.empty {
		color: var(--muted);
		font-size: 0.85rem;
	}
</style>
